<template>
  <header
    :class="['ui-topbar', { sticky: isSticky }]"
    id="header"
  >
    <!-- Left: menu (tablet/mobile) + brand + page search -->
    <div class="ui-topbar-left">
      <button
        class="ui-icon-btn ui-topbar-burger"
        id="header-burger-menu"
        type="button"
        :aria-label="$t('header.openMenu')"
        @click="stateStoreInstance.onChange"
      >
        <i class="ph ph-list" aria-hidden="true"></i>
      </button>
      <router-link to="/" class="ui-topbar-brand" :aria-label="$t('header.home')">
        <img :src="logoSrc" alt="" />
      </router-link>

      <div class="ui-search" ref="searchBoxRef">
        <i class="ph ph-magnifying-glass ui-search-icon" aria-hidden="true"></i>
        <input
          type="text"
          class="ui-search-input"
          :placeholder="$t('header.searchPlaceholder')"
          :aria-label="$t('header.searchLabel')"
          v-model="searchQuery"
          @focus="searchOpen = true"
          @keydown.escape="closeSearch"
          @keydown.enter="goToFirst"
          @keydown.down.prevent="highlightNext"
          @keydown.up.prevent="highlightPrev"
          autocomplete="off"
        />
        <div
          v-if="searchOpen && searchQuery.trim() && filteredPages.length"
          class="search-dropdown"
          role="listbox"
        >
          <div
            v-for="(page, i) in filteredPages"
            :key="page.path"
            class="search-result-item"
            :class="{ 'search-result-item--active': i === highlightIndex }"
            role="option"
            :aria-selected="i === highlightIndex"
            @mousedown.prevent="navigateTo(page.path)"
          >
            <i :class="page.icon" class="search-result-icon"></i>
            <span>{{ $t(page.name) }}</span>
            <i class="ph ph-arrow-right search-result-go" aria-hidden="true"></i>
          </div>
        </div>
        <div
          v-if="searchOpen && searchQuery.trim() && !filteredPages.length"
          class="search-dropdown"
        >
          <div class="search-no-result">{{ $t("header.noResult") }}</div>
        </div>
      </div>
    </div>

    <!-- Right: language, theme, notifications, profile -->
    <div class="ui-topbar-right">
      <LanguageSwitcher />
      <LightDarkSwtichBtn />

      <div class="dropdown">
        <button
          class="ui-icon-btn ui-topbar-bell"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-display="static"
          aria-expanded="false"
          :aria-label="$t('header.notifications')"
        >
          <i class="ph ph-bell" aria-hidden="true"></i>
          <span v-if="unreadCount" class="ui-topbar-count">{{ unreadCount > 99 ? "99+" : unreadCount }}</span>
        </button>
        <div class="dropdown-menu dropdown-menu-end ui-topbar-menu ui-alerts-menu">
          <div class="ui-topbar-menu-head">
            <span>{{ $t("header.notifications") }}</span>
            <span class="ui-topbar-menu-muted">({{ unreadCount }})</span>
            <button
              v-if="unreadCount"
              type="button"
              class="ui-alerts-readall"
              @click.stop="markAllRead"
            >
              {{ $t("header.markAllRead") }}
            </button>
          </div>
          <p v-if="!alerts.length" class="ui-topbar-menu-empty">{{ $t("header.noNotifications") }}</p>
          <div v-else class="ui-alerts-list">
            <button
              v-for="alert in alerts"
              :key="alert.id"
              type="button"
              :class="['ui-alert-item', { 'ui-alert-item--unread': !alert.read_at }]"
              @click="markRead(alert)"
            >
              <span class="ui-alert-title">{{ alert.title }}</span>
              <span class="ui-alert-body">{{ alert.body }}</span>
              <span class="ui-alert-date">{{ formatAlertDate(alert.created_at) }}</span>
            </button>
          </div>
          <router-link v-if="hasMoreAlerts" to="/notifications" class="ui-alerts-all">
            {{ $t("header.viewAllNotifications") }}
          </router-link>
        </div>
      </div>

      <div class="dropdown">
        <button
          class="ui-topbar-profile"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-display="static"
          aria-expanded="false"
        >
          <span class="ui-topbar-avatar">
            <img
              v-if="user?.pic"
              :src="user.pic"
              width="36"
              height="36"
              alt="admin"
            />
            <img
              v-else
              :src="require('@/assets/images/NoImage.png')"
              width="36"
              height="36"
              alt="admin"
            />
            <span class="ui-topbar-online" aria-hidden="true"></span>
          </span>
          <span class="ui-topbar-profile-text">
            <span class="ui-topbar-name">{{ user?.name }}</span>
            <span class="ui-topbar-role">{{ $t("header.roleAdmin") }}</span>
          </span>
          <i class="ph ph-caret-down ui-topbar-chevron" aria-hidden="true"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-end ui-topbar-menu">
          <div class="ui-topbar-menu-head ui-topbar-menu-user">
            <span class="text-truncate">{{ user?.name }}</span>
            <span class="ui-topbar-menu-online">{{ $t("header.onlineAccount") }}</span>
          </div>
          <router-link to="/profile" class="dropdown-item">
            <i class="ph ph-user-circle" aria-hidden="true"></i>
            {{ $t("header.myAccount") }}
          </router-link>
          <router-link to="/account-settings" class="dropdown-item">
            <i class="ph ph-gear" aria-hidden="true"></i>
            {{ $t("header.settings") }}
          </router-link>
          <button
            type="button"
            class="dropdown-item text-danger"
            @click="handleLogout()"
          >
            <i class="ph ph-sign-out" aria-hidden="true"></i>
            {{ $t("header.logout") }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import LightDarkSwtichBtn from "./LightDarkSwtichBtn.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import stateStore from "../../utils/store";
import { useAuthStore } from "@/stores/auth";
import { useSettingsStore } from "@/stores/settings";
import api from "@/utils/axios";
import { dateLocale } from "@/i18n";

interface UserAlert {
  id: number;
  title: string;
  body: string | null;
  parcel_code: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

const ALERTS_URL = "/api/auth/user-alerts";
const ALERTS_POLL_MS = 120000;

// `name` is a translation key
const ALL_PAGES = [
  { name: "menu.dashboard", path: "/", icon: "flaticon-home" },
  { name: "menu.parcelsList", path: "/colis-list", icon: "flaticon-sterile-box" },
  { name: "menu.addParcel", path: "/add-colis", icon: "flaticon-add" },
  { name: "menu.newParcels", path: "/new-colis-list", icon: "flaticon-sterile-box" },
  { name: "menu.customersList", path: "/customers-list", icon: "flaticon-users-group" },
  { name: "pages.addCustomer", path: "/add-customer", icon: "flaticon-add" },
  { name: "menu.newCustomers", path: "/new-customers-list", icon: "flaticon-users-group" },
  { name: "menu.usersList", path: "/users-list", icon: "flaticon-user-2" },
  { name: "pages.addUser", path: "/add-user", icon: "flaticon-add" },
  { name: "pages.pendingDrivers", path: "/new-driver-list", icon: "flaticon-fast-delivery" },
  { name: "menu.driversStatus", path: "/driver-returns-report", icon: "flaticon-fast-delivery" },
  { name: "menu.claims", path: "/claims-list", icon: "flaticon-information" },
  { name: "pages.pickups", path: "/pickup-list", icon: "flaticon-cube-3d" },
  { name: "menu.pickupRequests", path: "/requests-pickup-list", icon: "flaticon-cube-3d" },
  { name: "menu.deliveryNotes", path: "/delivery-notes", icon: "flaticon-document" },
  { name: "menu.deliveryStockNotes", path: "/delivery-stock-notes", icon: "flaticon-document" },
  { name: "menu.packingStation", path: "/packing", icon: "flaticon-cube-3d" },
  { name: "pages.receptionNotes", path: "/reception-notes", icon: "flaticon-draft" },
  { name: "menu.sentNotes", path: "/sent-notes", icon: "flaticon-telegram" },
  { name: "menu.distributionNotes", path: "/distribution-notes", icon: "flaticon-notes" },
  { name: "pages.driverPaymentNotes", path: "/payment-note-liste", icon: "flaticon-money" },
  { name: "pages.zonePaymentNotes", path: "/payment-zone-liste", icon: "flaticon-money" },
  { name: "menu.clientReturns", path: "/return-client-liste", icon: "flaticon-arrow-right-1" },
  { name: "menu.zoneReturns", path: "/return-zone-liste", icon: "flaticon-arrow-right-1" },
  { name: "pages.invoices", path: "/invoices-liste", icon: "flaticon-bill" },
  { name: "menu.transfers", path: "/virements-liste", icon: "flaticon-money" },
  { name: "menu.expenses", path: "/spents", icon: "flaticon-money" },
  { name: "pages.stockProducts", path: "/products-list", icon: "flaticon-parcel" },
  { name: "menu.stockParcels", path: "/colis-stock-list", icon: "flaticon-sterile-box" },
  { name: "pages.verifications", path: "/verifications", icon: "flaticon-check" },
  { name: "pages.accountSettings", path: "/account-settings", icon: "flaticon-setting" },
  { name: "menu.zones", path: "/zones-settings", icon: "flaticon-map" },
  { name: "menu.pricePacks", path: "/packs-settings", icon: "flaticon-money" },
  { name: "menu.cities", path: "/cities-settings", icon: "flaticon-map" },
  { name: "menu.agencies", path: "/agencies-settings", icon: "flaticon-home" },
  { name: "pages.statuses", path: "/settings-statuts", icon: "flaticon-setting" },
  { name: "pages.clientTypes", path: "/settings-type-clients", icon: "flaticon-setting" },
  { name: "pages.banks", path: "/settings-banks", icon: "flaticon-setting" },
  { name: "pages.transports", path: "/transports", icon: "ph ph-truck" },
  { name: "header.myProfile", path: "/profile", icon: "flaticon-user-2" },
  { name: "menu.ribRequests", path: "/rib-requests-list", icon: "flaticon-draft" },
  { name: "menu.phoneRequests", path: "/phone-requests-list", icon: "flaticon-draft" },
  { name: "menu.blackList", path: "/black-list", icon: "flaticon-setting" },
  { name: "pages.trackingDrivers", path: "/tracking-drivers", icon: "ph ph-navigation-arrow" },
  { name: "pages.refundRequests", path: "/refund-requests", icon: "flaticon-money" },
  { name: "menu.parcelFollowups", path: "/parcel-followups", icon: "flaticon-sterile-box" },
  { name: "menu.exchanges", path: "/exchanges", icon: "ph ph-arrows-left-right" },
  { name: "menu.driverReturnsFollowup", path: "/return-deadlines", icon: "flaticon-fast-delivery" },
  { name: "menu.whatsapp", path: "/whatsapp", icon: "ph ph-whatsapp-logo" },
  { name: "menu.ads", path: "/ads-settings", icon: "flaticon-setting" },
  { name: "pages.socialLinks", path: "/settings-social-links", icon: "flaticon-setting" },
  { name: "pages.deliveryProviders", path: "/settings-delivery-providers", icon: "ph ph-truck" },
  { name: "menu.carrierInvoices", path: "/transport-invoices", icon: "flaticon-bill" },
  { name: "pages.externDeliveryNotes", path: "/delivery-extern-notes", icon: "flaticon-document" },
  { name: "pages.pointageNotes", path: "/pointage-notes", icon: "flaticon-notes" },
  { name: "pages.addPointage", path: "/add-pointage-notes", icon: "flaticon-add" },
  { name: "pages.driverApiDocs", path: "/driver-status-api-docs", icon: "flaticon-document" },
  { name: "pages.addProduct", path: "/add-product", icon: "flaticon-add" },
  { name: "pages.addDeliveryStockNote", path: "/add-delivery-stock-notes", icon: "flaticon-add" },
  { name: "pages.addReceptionNote", path: "/add-reception-notes", icon: "flaticon-add" },
  { name: "pages.addSentNote", path: "/add-sent-notes", icon: "flaticon-add" },
  { name: "pages.addGlobalSentNote", path: "/add-global-sent", icon: "flaticon-add" },
  { name: "pages.addDistributionNote", path: "/add-distribution-notes", icon: "flaticon-add" },
  { name: "pages.addDriverPayment", path: "/add-payment-note", icon: "flaticon-add" },
  { name: "pages.addZonePayment", path: "/add-payment-zone", icon: "flaticon-add" },
  { name: "pages.addClientReturn", path: "/add-return-client", icon: "flaticon-add" },
  { name: "pages.addZoneReturn", path: "/add-return-zone", icon: "flaticon-add" },
  { name: "pages.addInvoice", path: "/add-invoices", icon: "flaticon-add" },
  { name: "pages.addVirement", path: "/add-virements", icon: "flaticon-add" },
  { name: "pages.addExpense", path: "/Newspent", icon: "flaticon-add" },
];

export default defineComponent({
  name: "MainHeader",
  components: {
    LightDarkSwtichBtn,
    LanguageSwitcher,
  },
  setup() {
    const { t } = useI18n();
    const stateStoreInstance = stateStore;
    const isSticky = ref(false);
    const auth = useAuthStore();
    const user = auth.user;
    const router = useRouter();
    // Settings are fetched by App.vue when the layout is shown.
    const settingsStore = useSettingsStore();
    const logoSrc = computed(() => settingsStore.logo || require("../../assets/images/logo.png"));

    // Search
    const searchQuery = ref("");
    const searchOpen = ref(false);
    const highlightIndex = ref(0);
    const searchBoxRef = ref<HTMLElement | null>(null);

    const filteredPages = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();
      if (!q) return [];
      return ALL_PAGES.filter((p) => t(p.name).toLowerCase().includes(q)).slice(0, 8);
    });

    function navigateTo(path: string) {
      router.push(path);
      closeSearch();
    }

    function goToFirst() {
      if (filteredPages.value.length) {
        navigateTo(filteredPages.value[highlightIndex.value]?.path ?? filteredPages.value[0].path);
      }
    }

    function closeSearch() {
      searchOpen.value = false;
      searchQuery.value = "";
      highlightIndex.value = 0;
    }

    function highlightNext() {
      if (highlightIndex.value < filteredPages.value.length - 1) highlightIndex.value++;
    }

    function highlightPrev() {
      if (highlightIndex.value > 0) highlightIndex.value--;
    }

    function handleClickOutside(e: MouseEvent) {
      if (searchBoxRef.value && !searchBoxRef.value.contains(e.target as Node)) {
        searchOpen.value = false;
      }
    }

    // In-app alerts (e.g. auto-returned parcels not handed back)
    const alerts = ref<UserAlert[]>([]);
    const unreadCount = ref(0);
    const hasMoreAlerts = ref(false);
    let lastAlertsFetch = 0;
    let alertsTimer: ReturnType<typeof setInterval> | undefined;

    async function fetchAlerts() {
      // no connected user (login page, logged out, token cleared): don't call the API
      if (!auth.isAuthenticated) {
        alerts.value = [];
        unreadCount.value = 0;
        return;
      }
      lastAlertsFetch = Date.now();
      try {
        const res = await api.get(ALERTS_URL);
        alerts.value = res.data.alerts ?? [];
        unreadCount.value = res.data.unread ?? 0;
        hasMoreAlerts.value = !!res.data.has_more;
      } catch {
        // the bell stays as it is; next poll retries
      }
    }

    async function markRead(alert: UserAlert) {
      if (alert.link) router.push(alert.link);
      if (alert.read_at) return;
      alert.read_at = new Date().toISOString();
      unreadCount.value = Math.max(0, unreadCount.value - 1);
      try {
        await api.post(`${ALERTS_URL}/${alert.id}/read`);
      } catch {
        fetchAlerts();
      }
    }

    async function markAllRead() {
      try {
        await api.post(`${ALERTS_URL}/read-all`);
      } finally {
        fetchAlerts();
      }
    }

    function formatAlertDate(value: string) {
      return new Date(value).toLocaleString(dateLocale(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    onMounted(() => {
      window.addEventListener("scroll", () => {
        isSticky.value = window.scrollY >= 100;
      });
      document.addEventListener("click", handleClickOutside);
      // random delay on first load so thousands of users don't hit the API on the same second
      setTimeout(fetchAlerts, Math.random() * 3000);
      // background tabs don't poll (many admins keep several tabs open)
      alertsTimer = setInterval(() => {
        if (document.visibilityState === "visible") fetchAlerts();
      }, ALERTS_POLL_MS);
      document.addEventListener("visibilitychange", onVisible);
    });

    // coming back to a tab: refresh only if the data is older than one poll period
    function onVisible() {
      if (document.visibilityState === "visible" && Date.now() - lastAlertsFetch > ALERTS_POLL_MS) fetchAlerts();
    }

    onUnmounted(() => {
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("click", handleClickOutside);
      clearInterval(alertsTimer);
    });

    async function handleLogout() {
      clearInterval(alertsTimer);
      await auth.logout();
      window.location.href = "/login";
    }

    return {
      isSticky,
      stateStoreInstance,
      user,
      logoSrc,
      handleLogout,
      searchQuery,
      searchOpen,
      searchBoxRef,
      filteredPages,
      highlightIndex,
      navigateTo,
      goToFirst,
      closeSearch,
      highlightNext,
      highlightPrev,
      alerts,
      unreadCount,
      hasMoreAlerts,
      markRead,
      markAllRead,
      formatAlertDate,
    };
  },
});
</script>

<style scoped>
.ui-alerts-all {
  display: block;
  padding: 10px 16px;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
  color: var(--brand-accent-text, var(--brand-accent, #ff5500));
  border-top: 1px solid var(--brand-border, #eee);
  text-decoration: none;
}
.ui-topbar {
  position: fixed;
  z-index: 1020;
  top: var(--ui-frame-gap);
  left: var(--ui-content-left);
  right: var(--ui-frame-gap);
  height: var(--ui-topbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-4);
  padding: 0 12px 0 16px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-bar);
  transition: box-shadow var(--ui-dur) var(--ui-ease), background-color var(--ui-dur) var(--ui-ease),
    left 280ms var(--ui-ease-spring), right 280ms var(--ui-ease-spring);
}
.ui-topbar.sticky {
  box-shadow: 0 14px 40px rgba(var(--ui-accent-rgb), 0.14);
}
:global(.dark) .ui-topbar.sticky {
  box-shadow: var(--ui-shadow-bar);
}

.ui-topbar-left,
.ui-topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.ui-topbar-brand {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: var(--ui-radius-md);
  transition: background-color var(--ui-dur-fast) var(--ui-ease);
}
.ui-topbar-brand:hover {
  background: var(--ui-surface-sunken);
}
.ui-topbar-brand img {
  height: 48px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

/* Notification bell with pulsing dot */
.ui-topbar-bell {
  position: relative;
}
.ui-topbar-bell:hover,
.ui-topbar-bell[aria-expanded="true"] {
  color: #FFFFFF;
  background: var(--ui-accent);
  border-color: var(--ui-accent-strong);
}
.ui-topbar-count {
  position: absolute;
  top: -3px;
  right: -3px;
  display: grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-accent);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: var(--ui-fw-black);
  box-shadow: 0 0 0 2px var(--ui-surface);
}

/* Profile pill */
.ui-topbar-profile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 14px 4px 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  text-align: left;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.ui-topbar-profile:hover,
.ui-topbar-profile[aria-expanded="true"] {
  background: var(--ui-surface);
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.12);
}
.ui-topbar-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--ui-accent);
  box-shadow: 0 4px 12px rgba(var(--ui-accent-rgb), 0.3);
}
.ui-topbar-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.ui-topbar-online {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 2px solid var(--ui-surface);
  background: var(--ui-success);
}
.ui-topbar-profile-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}
.ui-topbar-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.ui-topbar-role {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-semibold);
}
.ui-topbar-chevron {
  color: var(--ui-text-muted);
  font-size: 13px;
  transition: transform var(--ui-dur) var(--ui-ease);
}
.ui-topbar-profile[aria-expanded="true"] .ui-topbar-chevron {
  transform: rotate(180deg);
}

/* Dropdown panels */
.ui-topbar-right .dropdown {
  position: relative;
}
/* Static positioning (data-bs-display="static"): anchor under the toggle, right-aligned */
.ui-topbar-menu {
  position: absolute;
  top: 100% !important;
  right: 0 !important;
  left: auto !important;
  transform: none !important;
  min-width: 240px;
  margin-top: 10px !important;
}
.ui-topbar-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ui-topbar-menu .dropdown-item i {
  color: var(--ui-accent-text);
  font-size: 17px;
}
.ui-topbar-menu .dropdown-item.text-danger i {
  color: var(--ui-danger);
}
.ui-topbar-menu-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 8px 12px 10px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.ui-topbar-menu-user {
  flex-direction: column;
  gap: 2px;
}
.ui-topbar-menu-muted {
  color: var(--ui-text-muted);
  font-weight: var(--ui-fw-semibold);
}
.ui-topbar-menu-online {
  color: var(--ui-success);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.ui-topbar-menu-empty {
  margin: 0;
  padding: 14px 12px 10px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  text-align: center;
}

/* Alerts list */
.ui-alerts-menu {
  width: 340px;
  max-width: calc(100vw - 32px);
}
.ui-alerts-readall {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.ui-alerts-list {
  max-height: 360px;
  overflow-y: auto;
}
.ui-alert-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: var(--ui-radius-sm);
  background: none;
  color: var(--ui-text);
  text-align: left;
  white-space: normal;
}
.ui-alert-item:hover {
  background: var(--ui-surface-sunken);
}
.ui-alert-item--unread {
  background: var(--ui-accent-soft);
}
.ui-alert-title {
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.ui-alert-body {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
}
.ui-alert-date {
  color: var(--ui-text-faint);
  font-size: var(--ui-fs-micro);
}

/* Page search (admin) */
.ui-search {
  position: relative;
  flex: 0 1 320px;
  min-width: 0;
  margin-left: 6px;
}
.ui-search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-faint);
  font-size: 17px;
  pointer-events: none;
}
.ui-search-input {
  width: 100%;
  height: 44px;
  padding: 0 16px 0 44px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  outline: none;
  transition: background-color var(--ui-dur) var(--ui-ease), border-color var(--ui-dur) var(--ui-ease),
    box-shadow var(--ui-dur) var(--ui-ease);
}
.ui-search-input::placeholder {
  color: var(--ui-text-faint);
}
.ui-search-input:focus {
  border-color: var(--ui-accent);
  background: var(--ui-surface);
  box-shadow: var(--ui-focus-ring);
}
.search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 9999;
  padding: 6px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-elevated);
  box-shadow: var(--ui-shadow-pop);
  overflow: hidden;
  animation: ui-search-in var(--ui-dur) var(--ui-ease-spring);
}
.search-result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: var(--ui-radius-sm);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  cursor: pointer;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.search-result-item:hover,
.search-result-item--active {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.search-result-icon {
  flex-shrink: 0;
  width: 18px;
  color: var(--ui-text-muted);
  font-size: 15px;
  text-align: center;
}
.search-result-go {
  margin-left: auto;
  opacity: 0;
  font-size: 13px;
  transition: opacity var(--ui-dur-fast) var(--ui-ease);
}
.search-result-item:hover .search-result-icon,
.search-result-item--active .search-result-icon,
.search-result-item--active .search-result-go {
  color: var(--ui-accent-text);
  opacity: 1;
}
.search-no-result {
  padding: 12px 14px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  text-align: center;
}
@keyframes ui-search-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: none; }
}
@media (max-width: 991.98px) {
  .ui-search {
    display: none;
  }
}

/* Responsive */
@media (min-width: 1200px) {
  .ui-topbar-burger {
    display: none;
  }
}
@media (max-width: 767.98px) {
  .ui-topbar {
    height: 60px;
    padding: 0 8px 0 8px;
  }
  .ui-topbar-brand img {
    height: 38px;
    max-width: 120px;
  }
  .ui-topbar-profile {
    padding: 3px;
  }
  .ui-topbar-profile-text,
  .ui-topbar-chevron {
    display: none;
  }
  .ui-topbar-right {
    gap: 6px;
  }
  /* Phones: open menus as a full-width panel just below the header */
  .ui-topbar-menu {
    position: fixed !important;
    top: calc(var(--ui-frame-gap) + 60px) !important;
    left: var(--ui-frame-gap) !important;
    right: var(--ui-frame-gap) !important;
    width: auto !important;
    min-width: 0;
    max-width: none !important;
    max-height: calc(100vh - var(--ui-frame-gap) * 2 - 80px);
    overflow-y: auto;
  }
}
</style>
